import type { PaginatedList, Patrol, Person } from '@local/common-lib';
import {
  CheckCircle as AcceptedIcon,
  Cancel as DeclinedIcon,
  FlagCircle as DidNotShowIcon,
  Help as PendingIcon,
} from '@mui/icons-material';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime, Duration } from 'luxon';
import type { FC } from 'react';
import { DateCard } from '../../components/date/index.js';
import { TimeDurationCard } from '../../components/date/time-duration-card.js';
import { FetchOneOfAll } from '../../components/fetch/fetch-one.js';
import { PatrolStatusChips } from '../../components/patrol-status/index.js';

interface Props {
  handleSelection?: (patrolId: string) => void;
  hideUnstartedBadge?: boolean;
  hideWho: boolean;
  patrols: PaginatedList<Patrol.Type>;
}

const personRenderer = (person: Person.View): JSX.Element => (
  <>
    {person.firstName} {person.lastName.toUpperCase()}
  </>
);

export const WidePatrolList: FC<Props> = ({
  handleSelection,
  hideUnstartedBadge,
  hideWho,
  patrols,
}) => {
  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>Patrol</TableCell>
          <TableCell>When</TableCell>
          {!hideWho && <TableCell>Patrollers</TableCell>}
          {!hideWho && <TableCell>Guests</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {patrols.items.map((row) => {
          const patrollers = row.patrollers ?? {};
          const guests = row.guests ?? {};
          return (
            <TableRow
              key={row._id}
              onClick={() => {
                handleSelection?.(row._id);
              }}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: !handleSelection ? 'unset' : 'pointer',
              }}
            >
              <TableCell>
                <Box>{row.name}</Box>

                <PatrolStatusChips
                  hideUnstartedBadge={hideUnstartedBadge}
                  patrol={row}
                />
              </TableCell>
              <TableCell component="th" scope="row">
                <Stack direction="row" spacing={1}>
                  <DateCard date={DateTime.fromJSDate(row.when)} />
                  <TimeDurationCard
                    duration={Duration.fromObject({ hours: row.hours })}
                    time={DateTime.fromJSDate(row.when)}
                  />
                </Stack>
              </TableCell>
              {!hideWho && (
                <TableCell>
                  {Object.keys(patrollers).length ? (
                    <Stack direction="column" spacing="0.5rem">
                      {Object.keys(patrollers)
                        .filter((personId) => !patrollers[personId].removed)
                        .map((personId) => (
                          <div key={personId}>
                            {patrollers[personId].didNotShow ? (
                              <DidNotShowIcon
                                color="error"
                                sx={{
                                  fontSize: '1rem',
                                  mr: 0.5,
                                  verticalAlign: 'middle',
                                }}
                              />
                            ) : patrollers[personId].accepted ? (
                              <AcceptedIcon
                                color="success"
                                sx={{
                                  fontSize: '1rem',
                                  mr: 0.5,
                                  verticalAlign: 'middle',
                                }}
                              />
                            ) : patrollers[personId].declined ? (
                              <DeclinedIcon
                                color="error"
                                sx={{
                                  fontSize: '1rem',
                                  mr: 0.5,
                                  verticalAlign: 'middle',
                                }}
                              />
                            ) : (
                              <PendingIcon
                                color="disabled"
                                sx={{
                                  fontSize: '1rem',
                                  mr: 0.5,
                                  verticalAlign: 'middle',
                                }}
                              />
                            )}
                            <FetchOneOfAll
                              dataType="person"
                              id={personId}
                              renderFactory={personRenderer}
                            />
                          </div>
                        ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                      No patrollers
                    </Typography>
                  )}
                </TableCell>
              )}
              {!hideWho && (
                <TableCell>
                  {Object.keys(guests).length ? (
                    <Stack direction="column" spacing="0.5rem">
                      {Object.keys(guests)
                        .filter((guestId) => !guests[guestId].removed)
                        .map((guestId) => (
                          <div key={guestId}>{guests[guestId].name}</div>
                        ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                      No guests
                    </Typography>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
